/* External */
import {
  lazy, memo, Suspense, useCallback, useEffect, useMemo, useRef,
} from 'react';
import PropTypes from 'prop-types';
import {
  Switch, Route, Redirect, useLocation,
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

/* Components */
import { UPDATE_PROFILES } from '~/storage/profiles/actionTypes';
import ValidationPage from './ValidationPage';
import Interstitial from './Interstitial';
import Home from './Home';
import OnDemand from './OnDemand';
import Kids from './Kids';
import Lander from './Lander';
import LegoPlayer from './LegoPlayer';
import MyPage from './MyPage';
import TermsOfService from '../../components/TermsOfService';
import DetailSkeleton from '../../components/DetailSkeleton';
import LanderSkeleton from '../../components/LanderSkeleton';
import Sidebar from '../../components/Sidebar';

/* Providers */
import { useIsSidebarExpanded, useSetSidebarStateContext } from '../../providers/SidebarStateContext';

/* Storage */
import { selectShowModal } from '../../storage/modal/selectors';

/* Utils */
import { SIDEBAR } from '../../utils/focusKey';

/* Styles */
import './Private.scss';

// Hooks
import { useCloseAppCompletely } from '../../hooks/useShouldCloseAppCompletely';

/* Storage */
import { fetchAllChannels } from '../../storage/channels';
import { resetPdpHistory, resetBackButtonHistory } from '../../storage/pdpHistory';

/* Utils */
import { getLink, getPath, Routes } from '../../utils/router';
import { TYPE_ACTIONS_PROFILE } from '../../utils/constants';
import { featureFlagParentalControl, getProfileSelected } from '../../utils/profiles';
import platform from '../../utils/platform';
import {
  isSpideoMoreLikeThisActive,
  channelMinimumSizeToFetch,
  IS_LEGO_PLAYER_ACTIVE,
} from '../../utils/env';
import { selectAllChannels } from '../../storage/channels/selectors';

/* Providers */
import { useCountry } from '../../providers/ConfigProvider';
import {
  useIsLoggedIn, useIsLoadingUserAccount, useIsFrontPorchUser,
} from '../../providers/UserProvider';
import { MessageId } from '../../components/FullScreenMessage';
import { useOnBackground, useIsForegroundApp } from '../../providers/ForegroundAppProvider';
import { checkToken } from '../../services/authService';

/* Route-level code splitting: the player stack (Shaka + NPAW + PlayerUI) and
   both EPGs are the heaviest leaves of the private tree; the other heavy
   routes (profiles flow, search, detail, catalog, settings, my-list, terms)
   are only needed once their routes match, so they load on demand too. The
   landing graph (Home → LanderPage) stays static so boot renders the shell
   immediately. */
const Live = lazy(() => import('./Live'));
const Sports = lazy(() => import('./Sports'));
const Player = lazy(() => import('./Player'));
const Search = lazy(() => import('./Search'));
const Setting = lazy(() => import('./Setting'));
const Catalog = lazy(() => import('./Catalog'));
const Detail = lazy(() => import('./Detail'));
const MyList = lazy(() => import('./MyList'));
const AcceptTermsOfService = lazy(() => import('./AcceptTermsOfService'));
const TermsOfServiceErrorPage = lazy(() => import('./AcceptTermsOfService/TermsOfServiceErrorPage'));
const UserProfile = lazy(() => import('./Profiles/UserProfile/UserProfile'));
const Onboarding = lazy(() => import('./Profiles/Onboarding/Onboarding'));
const CreateProfileName = lazy(() => import('./Profiles/CreateProfileName/CreateProfileName'));
const CreateAllowedContent = lazy(() => import('./Profiles/CreateAllowedContent/CreateAllowedContent'));
const CreateProfileConfirmation = lazy(() => import('./Profiles/CreateProfileConfirmation/CreateProfileConfirmation'));
const CreateProfileResume = lazy(() => import('./Profiles/CreateProfileResume/CreateProfileResume'));
const ManagementProfile = lazy(() => import('./Profiles/ManagementProfile/ManagementProfile'));
const ConfirmDeleteProfile = lazy(() => import('./Profiles/ConfirmDeleteProfile/ConfirmDeleteProfile'));
const CreateProfile = lazy(() => import('./Profiles/CreateProfile/CreateProfile'));
const ProfilesErrorPage = lazy(() => import('./Profiles/ProfilesErrorPage/ProfilesErrorPage'));
const PlayerErrorPage = lazy(() => import('./Player/PlayerErrorPage/PlayerErrorPage'));
const DefaultCreateProfile = lazy(() => import('./Profiles/defaultProfileFlow/DefaultCreateProfile/DefaultCreateProfile'));
const DefaultCreateProfileName = lazy(() => import('./Profiles/defaultProfileFlow/DefaultCreateProfileName/DefaultCreateProfileName'));

const frontPorchMessageUrl = getLink(Routes.MessagePage, {
  query: { messageKey: MessageId.FRONT_PORCH },
});

const selectProfilesLink = getLink(
  Routes.Private.Profiles,
);

const minimumChannelSize = channelMinimumSizeToFetch || 5;

/* Static shell rendered while a lazy private route's chunk loads — see
   Private.scss. It renders the REAL Sidebar (so the menu stays visible and
   interactive during the transition) plus a skeleton shaped after the
   destination route: the LanderSkeleton for lander-style pages, the
   DetailSkeleton for the content-detail (VOD/Show/Live) pages, whose layout
   is nothing like a lander. Detail routes are the only private pages that
   do NOT render a sidebar, so the shell skips it for them — the shell then
   matches the page it is about to show. The sidebar registers the SIDEBAR
   focus key exactly like the routes' own LanderLayout; React unmounts this
   shell before the route's layout mounts, so the key is cleanly handed off
   (never two registrations at once). */
function PrivateRouteLoading({ isDetailPage = false }) {
  const isSidebarExpanded = useIsSidebarExpanded();
  const { openSidebar, closeSidebar } = useSetSidebarStateContext();
  const isModalShown = useSelector(selectShowModal) || false;

  return (
    <div className="dtv-html5-c-route-loading">
      {!isDetailPage && (
        <Sidebar
          className="dtv-html5-c-route-loading__sidebar"
          focusKey={SIDEBAR}
          open={openSidebar}
          close={closeSidebar}
          focusRestorable={isSidebarExpanded}
          expanded={isSidebarExpanded}
          isModalShown={isModalShown}
        />
      )}
      <div className="dtv-html5-c-route-loading__main">
        {isDetailPage ? <DetailSkeleton /> : <LanderSkeleton />}
      </div>
    </div>
  );
}

PrivateRouteLoading.propTypes = {
  isDetailPage: PropTypes.bool,
};

function Private() {
  const dispatch = useDispatch();
  const country = useCountry();
  const { pathname, search, hash } = useLocation();
  const isRefreshing = useRef(false);
  const loginLink = useMemo(() => getLink(
    Routes.Public.PinLogin,
    { query: { redirectTo: `${pathname}${search}${hash}` } },
  ), [hash, pathname, search]);

  const channels = useSelector(selectAllChannels);
  const isLoggedIn = useIsLoggedIn();
  const isLoadingUser = useIsLoadingUserAccount();
  const isFrontPorch = useIsFrontPorchUser();
  const hasParentalControl = featureFlagParentalControl(country);
  const isForegroundApp = useIsForegroundApp();
  const profileSelected = useMemo(getProfileSelected, [pathname]);
  const detailPageUrlRegex = useMemo(() => /.*\/detail\/.*/, []);
  const playerPageUrlRegex = useMemo(() => /.*\/player\/.*/, []);
  const isNotDetailPageAndPlayer = !detailPageUrlRegex.test(pathname)
    && !playerPageUrlRegex.test(pathname);
  /* Content-detail (PDP) pages have their own layout — the route-loading
     shell swaps its skeleton to DetailSkeleton for them. */
  const isDetailPdpUrl = useMemo(() => /\/detail\/(vod|shows|live)\//.test(pathname), [pathname]);
  useEffect(() => {
    if (isSpideoMoreLikeThisActive && isNotDetailPageAndPlayer) {
      dispatch(resetPdpHistory());
      dispatch(resetBackButtonHistory());
    }
  }, [dispatch, isNotDetailPageAndPlayer]);

  useEffect(() => {
    if (!isLoadingUser && isLoggedIn && channels?.size <= minimumChannelSize) {
      dispatch(fetchAllChannels());
    }
  }, [dispatch, isLoadingUser, isLoggedIn, channels, pathname]);

  useEffect(() => {
    const performCheckToken = async () => {
      if (!isLoggedIn || isRefreshing.current || !isForegroundApp) return;

      isRefreshing.current = true;
      try {
        const updateProfiles = await checkToken();
        if (updateProfiles) {
          dispatch({ type: UPDATE_PROFILES });
        }
      } finally {
        isRefreshing.current = false;
      }
    };

    performCheckToken();
  }, [isLoggedIn, pathname, isForegroundApp, dispatch]);

  const shouldCloseAppCompletely = useCloseAppCompletely();

  const conditionallyCloseApp = useCallback(() => {
    if (shouldCloseAppCompletely) {
      platform.closeApp();
    }
  }, [shouldCloseAppCompletely]);

  useOnBackground(conditionallyCloseApp);

  /* eslint-disable-next-line unicorn/no-null
      -- React v16 doesn't support returning undefined in components */
  if (isLoadingUser) return null;

  return (
    /* Lazy routes render their own loaders; the fallback here is a static
       shell (sidebar + skeleton shaped after the destination route) so route
       changes never blank the screen while a chunk loads — see
       PrivateRouteLoading. */
    <Suspense fallback={<PrivateRouteLoading isDetailPage={isDetailPdpUrl} />}>
      <Switch>
        {!isLoggedIn && <Redirect to={loginLink} />}
        {!profileSelected
          && pathname !== selectProfilesLink
          && <Redirect to={selectProfilesLink} />}
        {isFrontPorch && <Redirect to={frontPorchMessageUrl} />}
        <Route exact path={getPath(Routes.Private)}>
          <ValidationPage />
        </Route>
        <Route path={getPath(Routes.Private.Interstitial)}>
          <Interstitial />
        </Route>
        <Route exact path={getPath(Routes.Private.AcceptTermsOfService)}>
          <AcceptTermsOfService />
        </Route>
        <Route exact path={getPath(Routes.Private.AcceptTermsOfService.Error)}>
          <TermsOfServiceErrorPage />
        </Route>
        <Route exact path={getPath(Routes.Private.TermsOfService)}>
          <TermsOfService />
        </Route>
        <Route exact path={getPath(Routes.Private.Home)}>
          <Home />
        </Route>
        <Route path={getPath(Routes.Private.Live)}>
          <Live />
        </Route>
        <Route path={getPath(Routes.Private.OnDemand)}>
          <OnDemand />
        </Route>
        <Route path={getPath(Routes.Private.Sports)}>
          <Sports />
        </Route>
        <Route path={getPath(Routes.Private.Kids)}>
          <Kids />
        </Route>
        <Route exact path={getPath(Routes.Private.Search)}>
          <Search />
        </Route>
        <Route path={getPath(Routes.Private.Setting)}>
          <Setting />
        </Route>
        <Route path={getPath(Routes.Private.Player.Error)}>
          <PlayerErrorPage />
        </Route>
        <Route path={getPath(Routes.Private.Player)}>
          <Player />
        </Route>
        <Route path={getPath(Routes.Private.Detail)}>
          <Detail />
        </Route>
        <Route path={getPath(Routes.Private.Catalog)}>
          <Catalog />
        </Route>
        <Route path={getPath(Routes.Private.MyList)}>
          <MyList />
        </Route>
        <Route path={getPath(Routes.Private.MyPage)}>
          <MyPage />
        </Route>
        <Route path={getPath(Routes.Private.Lander)}>
          <Lander />
        </Route>
        {IS_LEGO_PLAYER_ACTIVE && (
          <Route path={getPath(Routes.Private.LegoPlayer)}>
            <LegoPlayer />
          </Route>
        )}
        <Route exact path={getPath(Routes.Private.Profiles)}>
          <UserProfile />
        </Route>
        <Route path={getPath(Routes.Private.Onboarding)}>
          <Onboarding />
        </Route>
        <Route exact path={getPath(Routes.Private.Profiles.Management)}>
          <ManagementProfile />
        </Route>
        <Route path={getPath(Routes.Private.Profiles.Delete)}>
          <ConfirmDeleteProfile />
        </Route>
        <Route exact path={getPath(Routes.Private.Profiles.Edit)}>
          {hasParentalControl
            ? <CreateProfile typeAction={TYPE_ACTIONS_PROFILE.EDIT} />
            : <DefaultCreateProfile typeAction={TYPE_ACTIONS_PROFILE.EDIT} />}
        </Route>
        <Route exact path={getPath(Routes.Private.Profiles.Create)}>
          {hasParentalControl
            ? <CreateProfile typeAction={TYPE_ACTIONS_PROFILE.CREATE} />
            : <DefaultCreateProfile typeAction={TYPE_ACTIONS_PROFILE.CREATE} />}
        </Route>
        <Route path={getPath(Routes.Private.Profiles.Create.Name)}>
          {hasParentalControl
            ? <CreateProfileName />
            : <DefaultCreateProfileName />}
        </Route>
        <Route path={getPath(Routes.Private.Profiles.Create.AllowedContent)}>
          <CreateAllowedContent />
        </Route>
        <Route path={getPath(Routes.Private.Profiles.Create.Confirmation)}>
          <CreateProfileConfirmation />
        </Route>
        <Route path={getPath(Routes.Private.Profiles.Create.Resume)}>
          <CreateProfileResume />
        </Route>
        <Route path={getPath(Routes.Private.Profiles.Edit.Name)}>
          {hasParentalControl
            ? <CreateProfileName />
            : <DefaultCreateProfileName />}
        </Route>
        <Route path={getPath(Routes.Private.Profiles.Edit.AllowedContent)}>
          <CreateAllowedContent />
        </Route>
        <Route path={getPath(Routes.Private.Profiles.Error)}>
          <ProfilesErrorPage />
        </Route>
      </Switch>
    </Suspense>
  );
}

export default memo(Private);
